import React, { useState } from 'react'

import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  DialogContentText,
  MenuItem,
  Typography
} from "@mui/material";
import {useDispatch, useSelector} from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import Alert from '@mui/material/Alert';
import { zeroOrPositiveNumber } from '../../../utils/utils';
import {IApplicationCustomerMergeInfo} from "../../../Interfaces/Common/IApplicationCustomerMergeInfo";
import {Constants} from "../../../utils/constants";
import {DialogProps} from "../../Dialogs/DialogProps";
import {useConfiguration} from "../../../Data/ConfigurationData";
import {fetchData} from "../../../Data/fetch";
import {ApplicationDispatch, SET_SERVER_MESSAGE} from "../../../store/application/types";
import ErrorDialog from "../../Dialogs/ErrorDialog";
import {useDialog} from "../../../providers/DialogProvider";
import Divider from "@mui/material/Divider";
import { isEmpty } from 'ramda';

interface MergeAppDialogProps extends DialogProps<null> {
  onOK(applicantsToMerge: IApplicationCustomerMergeInfo[]): void;
}

const MergeAppDialog: React.FC<MergeAppDialogProps> = (props) => {
  const [appNo, setAppNo] = useState<number>(0);
  const { data: configuration } = useConfiguration();
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch<ApplicationDispatch>();
  const [isSearchButtonEnabled, setIsSearchButtonEnabled] = useState<boolean>(true);
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const applicantsToMergeFromCurrentApp = currentApp?.appCustomers.map(ac => {
    return {appCustomerId: ac.id, applicantFullName: `${ac.customer.firstName} ${ac.customer.lastName}`, customerRoleId: ac.customerRoleId, relationshipToApplicantId: ''};
  }) ?? [];
  const [applicantsToMerge, setApplicantsToMerge] = useState<IApplicationCustomerMergeInfo[]>(applicantsToMergeFromCurrentApp);
  const [areMergeableApplicants, setAreMergeableApplicants] = useState<boolean>(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState<string>("");
  const maxNumberOfApplicants = configuration?.maxNumberOfApplicants ?? 2;
  const applicantRoles = [
    {name: "Primary Applicant", id: Constants.roleApplicantId.toLowerCase()},
    {name: "Co-Applicant 1", id: Constants.roleCoApplicant1Id.toLowerCase()},
    maxNumberOfApplicants > 2 ? {name: "Co-Applicant 2", id: Constants.roleCoApplicant2Id.toLowerCase()} : {}
  ];
  const lookups = useSelector((state: AppState) => state.applications.lookups);

  // If there is more than one applicant with the same role, it returns true
  const isMultipleApplicantsForSameRole = (newApplicationCustomerMergeInfos: IApplicationCustomerMergeInfo[]): boolean => {
    let isError = false;
    applicantRoles?.forEach(ar => {
      if (newApplicationCustomerMergeInfos.filter(ami => ami.customerRoleId === ar.id?.toLowerCase()).length > 1) {
        isError = true;
      }
    })
    return isError;
  }
  
  // Current application number used for enabling/disabling the "OK" button.
  const currentAppNumber: number = currentApp?.applicationNo!;
  
  // Validator for when they click the OK button
  const validateDialog = (): string[] => {
    const errors: string[] = [];
    const numberOfPrimaries = applicantsToMerge.filter(atm => atm.customerRoleId === Constants.roleApplicantId.toLowerCase())?.length ?? 0;
    const numberOfCo1s = applicantsToMerge.filter(atm => atm.customerRoleId === Constants.roleCoApplicant1Id.toLowerCase())?.length ?? 0;
    const numberOfCo2s = applicantsToMerge.filter(atm => atm.customerRoleId === Constants.roleCoApplicant2Id.toLowerCase())?.length ?? 0;
    
    if (applicantsToMerge.length <= 1) {
      errors.push("There have to be at least 2 applicants to merge");
    }
    
    // If there are two applicants to merge and the two slots aren't taken up by the primary and co-1 roles
    if (applicantsToMerge.length === 2 
      && (numberOfPrimaries === 0 || numberOfCo1s === 0)) {
      errors.push("You have to select a Primary and a Co-applicant 1");
    }

    // If there are three applicants to merge and the three slots aren't taken up by the primary, co-1, and co-2 roles
    if (applicantsToMerge.length === 3
      && (numberOfPrimaries === 0 || numberOfCo1s === 0 || numberOfCo2s === 0)) {
      errors.push("You have to select a Primary, a Co-applicant 1, and a Co-applicant 2");
    }
    
    return errors;
  };
  
  // User has clicked the OK button.
  const handleOK = () => {
    const errors = validateDialog();
    if (errors.length > 0){
      openDialog({
        children: (
          <ErrorDialog
            title={"Merge Validation Errors"}
            errors={errors}
            closeDialog={closeDialog}
          />
        )
      });
    } else {
      props.onOK(applicantsToMerge);
      props.closeDialog();
    }
  }
  
  // Fires whenever user enters a number into the input field.
  const handleTextInput = (input: number): void => {
    setAppNo(input);
    // Disable button if user enters the same app number as the one they are currently on.
    setIsSearchButtonEnabled(input != currentAppNumber);
  }
  
  const handleSearch = async () => {
    try {
      const url = `api/Application/applicationToMergeWith/${appNo}`;
      const response = await fetchData(url, dispatch);
      if (response.error) {
        setSearchErrorMessage(`Error- ${response.error}`);
        setApplicantsToMerge([...applicantsToMergeFromCurrentApp]);
        setAreMergeableApplicants(false);
      } else {
        const newApplicationMergeInfos = [...applicantsToMergeFromCurrentApp, ...response];
        setSearchErrorMessage("");
        setApplicantsToMerge(newApplicationMergeInfos);
        setAreMergeableApplicants(response.length > 0);
      }
    } catch (err) {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: err as string},
      });
    }
  }

  const handleRoleChange = (appCustomerId: string, newCustomerRoleId: string) => {
    const newApplicantsToMerge = [...applicantsToMerge.map(atm => {
      if (atm.appCustomerId === appCustomerId){
        return {
          ...atm,
          customerRoleId: newCustomerRoleId
        }
      } else {
        return atm;
      }
    })];
    setApplicantsToMerge(newApplicantsToMerge);
  }

  const handleRelationshipChange = (newCustomerRelationshipId: string) => {
    const newApplicantsToMerge = [...applicantsToMerge.map(atm => {
        return {
          ...atm,
          relationshipToApplicantId: newCustomerRelationshipId
        }
    })];
    setApplicantsToMerge(newApplicantsToMerge);
  }

  const isRelationshipToApplicantError = (newApplicationCustomerMergeInfos: IApplicationCustomerMergeInfo[]): boolean => {
    let isError = true;
    newApplicationCustomerMergeInfos?.forEach(ar => {
      if ( !isEmpty(ar.relationshipToApplicantId) && ar.relationshipToApplicantId !== null) {
        isError = false;
      }
    })
    return isError;
  }
  
  const numberOfApplicantsTooLowError = applicantsToMerge.length < 2;
  const numberOfApplicantsTooHighError = applicantsToMerge.length > maxNumberOfApplicants;
  const invalidApplicantRolesError = isMultipleApplicantsForSameRole(applicantsToMerge);
  const relationshipToApplicantError = isRelationshipToApplicantError(applicantsToMerge);
  
  return (
    <>
      <DialogTitle id="form-dialog-title">Merge Application</DialogTitle>
      <DialogContent style={{maxWidth: "450px"}}>
        <DialogContentText>
          Please enter the application number to merge
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          id="app-no"
          label="Application Number to Merge"
          type="number"
          value={appNo}
          onChange={(e) => handleTextInput(zeroOrPositiveNumber(e.currentTarget.value))}
          style={{width: "250px"}}
        />
        <Button 
          variant={"contained"} 
          style={{marginLeft: "10px", marginTop: "10px"}}
          onClick={handleSearch}
        >
          Search
        </Button>
        {
          !isSearchButtonEnabled && (
            <Alert severity="error">Invalid app number (you are on app #{appNo})</Alert>
          )
        }
        {searchErrorMessage &&
          <Typography variant="subtitle1" style={{color: "red"}}>
            {searchErrorMessage}
          </Typography>
        }
        {areMergeableApplicants &&
          <>
            <Divider variant="fullWidth" style={{marginTop: "10px"}}/>
            <Typography variant="subtitle1" style={{marginTop: "10px"}}>
              Select the new role for each applicant
            </Typography>
          </>
        }
        {areMergeableApplicants && applicantsToMerge.map((atm, key) => {
          return (
            <TextField
              key={key}
              style={{textAlign: 'right', marginTop: '10px', width: "350px"}}
              label={`New role for ${atm.applicantFullName}`}
              value={atm.customerRoleId}
              fullWidth
              size='small'
              variant='outlined'
              select
              error={numberOfApplicantsTooHighError || invalidApplicantRolesError}
              onChange={(e) => handleRoleChange(atm.appCustomerId, e.target.value)}
            >
              {applicantRoles?.map((ar, key) => 
                <MenuItem key={key} value={ar.id}>{ar.name}</MenuItem>
              )}
            </TextField>
          )
        })}
        {areMergeableApplicants &&
        (
            <>
            <Divider variant="fullWidth" style={{marginTop: "10px"}}/>
            <TextField
              style={{textAlign: 'right', marginTop: '10px', width: "350px"}}
              label={`Co-Applicant's Relationship to Applicant`}
              fullWidth
              size='small'
              variant='outlined'
              select
              error={relationshipToApplicantError}
              onChange={(e) => handleRelationshipChange(e.target.value)}
            >
              {lookups.relationshipToApplicants.map((rel, key) => (
                <MenuItem key={key} value={rel.id}>{`${rel.name}`}</MenuItem>))
              }
            </TextField>
          </>
        )}
        {numberOfApplicantsTooHighError &&
          <Typography variant="subtitle1" style={{color: "red"}}>
            {`The number of applicants (${applicantsToMerge.length}) exceeds the amount able to be merged (${maxNumberOfApplicants})`}
          </Typography>
        }
        {invalidApplicantRolesError &&
          <Typography variant="subtitle1" style={{color: "red"}}>
            {`There can't be multiple applicants with the same role`}
          </Typography>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOK} color="primary" disabled={numberOfApplicantsTooLowError || numberOfApplicantsTooHighError ||
             invalidApplicantRolesError || relationshipToApplicantError}>
          OK
        </Button>
        <Button onClick={props.closeDialog} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </>
  )
}

export default MergeAppDialog;